import React, { lazy, Suspense } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import StickyBox from 'react-sticky-box';

import { HEADER_OFFSET_SEARCH, TOP_OFFSET } from 'src/constants/header';
import { SEARCH_TYPE_NAMES } from 'src/constants/search';
import { LocalizationContext } from '@lt/localization/dist/context';

import type { IBrowser } from 'redux-responsive';
import type { SearchType } from 'src/types/search';
import ErrorBoundary from 'src/error/components/ErrorBoundary';
import SearchPageHeader from '../../SearchPageHeader';
import Hotels from '../../Hotels';

import LazySubscribe from '../../../containers/Subscribe/LazySubscribe';
import LazyFilters from '../../Filters/components/FiltersPanel';
import SmallMap from '../../SmallMap';

const EmptySearch = lazy(
  () => import(/* webpackChunkName: "EmptySearch" */ '../../EmptySearch'),
);

const EmptyFiltration = lazy(
  () =>
    import(/* webpackChunkName: "EmptyFiltration" */ '../../EmptyFiltration'),
);

const YandexMapsSearch = lazy(
  () =>
    import(/* webpackChunkName: "YandexMapsSearch" */ '../../YandexMapsSearch'),
);

const OperatorsStatuses = lazy(
  () =>
    import(
      /* webpackChunkName: "OperatorsStatuses" */ '../../OperatorsStatuses'
    ),
);

type ISearchPageDesktop = {
  firstResultsFetched: boolean;
  hideSidebar: boolean;
  browser: IBrowser;
  mapView: boolean;
  isAdmin: boolean;
  isShowSubscription: boolean;
  isEmptyFiltration: boolean;
  isEmptySearch: boolean;
  searchType: SearchType;
};

function SearchPageDesktop(props: ISearchPageDesktop) {
  const {
    browser,
    hideSidebar,
    mapView,
    isAdmin = false,
    isShowSubscription,
    isEmptyFiltration,
    isEmptySearch,
    searchType,
    firstResultsFetched,
  } = props;

  const hasResult = !(isEmptyFiltration || isEmptySearch);
  const showSubscribeWidget = isShowSubscription && !isEmptySearch;
  const isHideMap = !browser.greaterThan.large;

  const sidebarClasses = 'search-page__sidebar';

  const contentClasses = classNames({
    'search-page__center': true,
    'hide-map': isHideMap,
    'search-page__center--open-filters': browser.lessThan.large && !hideSidebar,
  });
  const isEmptyResult = isEmptySearch || isEmptyFiltration;

  return (
    <div>
      <div className="search-page__content">
        <aside className={sidebarClasses}>
          <StickyBox offsetTop={HEADER_OFFSET_SEARCH + TOP_OFFSET}>
            <LocalizationContext.Consumer>
              {(store) =>
                showSubscribeWidget && store?.state.country === 'ru' ? (
                  <SubscribeCotainer>
                    <LazySubscribe />
                  </SubscribeCotainer>
                ) : null
              }
            </LocalizationContext.Consumer>
            {!mapView && browser.lessThan.extraLarge ? (
              <SmallMapStyled />
            ) : null}
            <LazyFilters
              show={firstResultsFetched}
              isHotelOnly={searchType === SEARCH_TYPE_NAMES.HOTEL}
            />
            <ErrorBoundary showDialog={false}>
              <Suspense fallback={null}>
                {isAdmin && <OperatorsStatuses />}
              </Suspense>
            </ErrorBoundary>
          </StickyBox>
        </aside>

        <div className={contentClasses}>
          {!isHideMap ? (
            <Container>
              <MainContainerWithFullMap mapView={mapView}>
                {!isEmptyResult && <SearchPageHeader />}
                <Suspense fallback={null}>
                  {isEmptySearch && <EmptySearch />}
                  {isEmptyFiltration && <EmptyFiltration />}
                </Suspense>

                {hasResult && <Hotels />}
              </MainContainerWithFullMap>
              <SearchPageMap>
                <Suspense fallback={null}>
                  <YandexMapsSearch />
                </Suspense>
              </SearchPageMap>
            </Container>
          ) : (
            <div>
              {!isEmptyResult && <SearchPageHeader />}
              {mapView ? (
                <SearchPageMap>
                  <Suspense fallback={null}>
                    <YandexMapsSearch />
                  </Suspense>
                </SearchPageMap>
              ) : (
                <div style={{ flex: 1 }}>
                  <Suspense fallback={null}>
                    {isEmptySearch && <EmptySearch />}
                    {isEmptyFiltration && <EmptyFiltration />}
                  </Suspense>

                  {hasResult && <Hotels />}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const SearchPageMap = styled.div`
  min-width: 0;
  position: relative;
  top: 0px;
  flex: 1 1 auto;
`;

const Container = styled.div`
  display: flex;
  height: calc(100% - 94px);
`;

const MainContainerWithFullMap = styled.div<{ mapView: boolean }>`
  padding: 0 8px;
  width: ${({ mapView }) => (mapView ? '320px' : '700px')};
`;

const SubscribeCotainer = styled.div`
  margin-bottom: 20px;
`;

const SmallMapStyled = styled(SmallMap)`
  margin-bottom: 16px;
`;

export default SearchPageDesktop;
